import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TimerOutlined as TimerOutlinedIcon,
} from 'views/icons';
import {Translate, translate} from 'views/module';

import {getActionLabel, getListDetail} from './agentJobTypes';
import {sortAgentJobsByExecutionTime} from './agentJobTypeUtils';

import moment from 'moment';

const statusStyles = {
  COMPLETED: {
    backgroundColor: '#3CAD65',
    color: '#FFFFFF',
    Icon: CheckIcon,
  },
  CANCELLED: {
    backgroundColor: '#F48A62',
    color: '#FFFFFF',
    Icon: CloseIcon,
  },
  FAILED: {
    backgroundColor: '#D34A2C',
    color: '#FFFFFF',
    Icon: ErrorOutlineIcon,
  },
  default: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #D9DCE5',
    color: '#9A9BAE',
    Icon: RadioButtonUncheckedIcon,
  },
};

const getFallbackTitle = (job) => {
  const actionLabel = getActionLabel(job);

  switch (job.status) {
    case 'COMPLETED':
      return translate('case.Detail.AgentJobs.List.CompletedTitle', {action: actionLabel});
    case 'CANCELLED':
      return translate('case.Detail.AgentJobs.List.CancelledTitle', {action: actionLabel});
    case 'FAILED':
      return translate('case.Detail.AgentJobs.List.FailedTitle', {action: actionLabel});
    default:
      return actionLabel;
  }
};

const getJobTitle = (job) => {
  return job.reasoning || getFallbackTitle(job);
};

const getStatusIcon = (job, style) => {
  if (job.actionType === 'TIMER' && job.status === 'COMPLETED') {
    return TimerOutlinedIcon;
  }

  return style.Icon;
};

const getExecutedAtLabel = (job) => {
  const displayMoment = moment(job.executeAt);

  if (!displayMoment.isValid()) {
    return '';
  }

  const timeLabel = displayMoment.format('h:mm A');

  if (displayMoment.isSame(moment(), 'day')) {
    return timeLabel;
  }

  const dateLabel = displayMoment.isSame(moment(), 'year') ?
    displayMoment.format('MMM D') :
    displayMoment.format('MMM D, YYYY');

  return `${dateLabel}\n${timeLabel}`;
};

const AgentJobsList = (props) => {
  const {
    agentJobs,
    participantDetails = {},
  } = props;

  const sortedJobs = React.useMemo(() => sortAgentJobsByExecutionTime(agentJobs), [agentJobs]);

  if (agentJobs.length === 0) {
    return null;
  }

  return (
    <Stack sx={{gap: 1.25}}>
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 800,
          letterSpacing: 0,
          textTransform: 'uppercase',
          color: '#172033',
        }}
      >
        <Translate expression='case.Detail.AgentJobs.List.Title' />
      </Typography>
      <Stack sx={{gap: 0}}>
        {
          sortedJobs.map((job, index) => {
            const executeAtMoment = moment(job.executeAt);
            const executeAtDisplayLabel = getExecutedAtLabel(job);
            const executeAtDetailLabel = executeAtMoment.isValid() ? executeAtMoment.format('MMM D, h:mm A') : '';
            const style = statusStyles[job.status] || statusStyles.default;
            const StatusIcon = getStatusIcon(job, style);
            const isLast = index === sortedJobs.length - 1;
            const detail = getListDetail(job, participantDetails, executeAtDetailLabel);

            return (
              <Box
                key={job.agentJobRn}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '58px 18px minmax(0, 1fr)',
                  columnGap: 0.85,
                  minHeight: '42px',
                }}
              >
                <Typography
                  sx={{
                    pt: 0.1,
                    fontSize: '9px',
                    fontWeight: 600,
                    color: job.status === 'CANCELLED' ? '#E4875B' : '#9A9BAE',
                    textAlign: 'right',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {executeAtDisplayLabel}
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {
                    !isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '17px',
                          bottom: 0,
                          width: '1px',
                          backgroundColor: '#E7E8EE',
                        }}
                      />
                    )
                  }
                  <Box
                    sx={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      backgroundColor: style.backgroundColor,
                      color: style.color,
                      border: style.border || 'none',
                    }}
                  >
                    <StatusIcon sx={{fontSize: job.status === 'CANCELLED' ? '10px' : '9px'}} />
                  </Box>
                </Box>
                <Box sx={{minWidth: 0, pb: isLast ? 0 : 1.1}}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 800,
                      lineHeight: '15px',
                      color: '#071126',
                      wordBreak: 'break-word',
                    }}
                  >
                    {getJobTitle(job)}
                  </Typography>
                  {
                    detail && (
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: '10px',
                          fontWeight: 600,
                          lineHeight: '13px',
                          color: job.status === 'CANCELLED' ? '#E4875B' : '#8F96A3',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {detail}
                      </Typography>
                    )
                  }
                </Box>
              </Box>
            );
          })
        }
      </Stack>
    </Stack>
  );
};

AgentJobsList.propTypes = {
  agentJobs: PropTypes.array.isRequired,
  participantDetails: PropTypes.object,
};

export default React.memo(AgentJobsList);
